'use client';

import { RefObject } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { alpha, Box, IconButton, InputAdornment, TextField, Typography } from '@mui/material';

import { useT } from '@/hooks';

interface SearchBarProps {
  searchInputRef: RefObject<HTMLDivElement>;
  onSearchClick: (e: React.MouseEvent | React.FocusEvent) => void;
  value: string;
  onChange: (value: string) => void;
  onSubmit?: (value: string) => void;
}

export const SearchBar = ({
  searchInputRef,
  onSearchClick,
  value,
  onChange,
  onSubmit,
}: SearchBarProps) => {
  const { t } = useT();
  return (
    <Box
      ref={searchInputRef}
      sx={{
        display: { xs: 'none', md: 'flex' },
        flex: 1,
        justifyContent: 'center',
        maxWidth: 600,
        mx: { md: 2, lg: 3 },
        position: 'relative',
        minWidth: 0,
      }}
    >
      <TextField
        size="small"
        placeholder={t('common.searchPorvenix')}
        fullWidth
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onSubmit?.(value);
          }
        }}
        onClick={onSearchClick}
        onFocus={onSearchClick}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {value ? (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange('');
                  }}
                  sx={{
                    p: 0.5,
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'text.primary',
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 0.75,
                    py: 0.25,
                    borderRadius: '4px',
                    bgcolor: 'action.hover',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 500 }}
                  >
                    /
                  </Typography>
                </Box>
              )}
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            bgcolor: (theme) =>
              theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
            borderRadius: '10px',
            height: '42px',
            fontSize: '0.9375rem',
            border: '1px solid',
            borderColor: (theme) =>
              theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200],
            transition: 'all 0.2s ease',
            '& fieldset': {
              borderColor: 'transparent',
            },
            '&:hover': {
              bgcolor: (theme) =>
                theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
              borderColor: (theme) =>
                theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
            },
            '&:hover fieldset': {
              borderColor: 'transparent',
            },
            '&.Mui-focused': {
              bgcolor: (theme) =>
                theme.palette.mode === 'dark'
                  ? theme.palette.grey[900]
                  : theme.palette.common.white,
              borderColor: 'primary.main',
              boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main',
              borderWidth: '1px',
            },
          },
          '& .MuiOutlinedInput-input': {
            color: 'text.primary',
            '&::placeholder': {
              color: 'text.secondary',
              opacity: 0.8,
            },
          },
        }}
      />
    </Box>
  );
};
